import {Country, useCountriesQuery} from 'api/generated';
import {useEffect, useMemo, useState} from 'react';

export const useCountries = () => {
  const [isoCountriesMap, setIsoCountriesMap] = useState<any | null>(null);
  const {data, loading} = useCountriesQuery();
  const isCountrySupported = data?.countries.isSupported;
  const currentCountry = data?.countries.currentCountry;
  const supportedCountries = data?.countries.supportedCountries || [];
  const supportedCountriesMap = useMemo(
    () =>
      supportedCountries.reduce<{[key: string]: Country}>((result, country) => {
        result[country.code] = country;
        return result;
      }, {}),
    [supportedCountries]
  );

  const isoCountries = useMemo(
    () =>
      isoCountriesMap
        ? Object.keys(isoCountriesMap).map((code) => ({code, name: isoCountriesMap[code].name}))
        : [],
    [isoCountriesMap]
  );

  useEffect(() => {
    import('../assets/iso-3166-2.json').then(({default: data}) => {
      setIsoCountriesMap(data);
    });
  });

  const getIsoCountry = (countryCode?: string | null) => {
    if (!isoCountriesMap || !countryCode) return null;
    const country = isoCountriesMap[countryCode];
    if (country) return country;
    return null;
  };

  const getCountryName = (countryCode?: string | null) => {
    const country = getIsoCountry(countryCode);
    return country?.name ?? null;
  };

  const getSupportedCountry = (countryCode?: string | null) => {
    if (!countryCode) return null;
    return supportedCountriesMap[countryCode] || null;
  };

  return {
    isoCountries,
    getIsoCountry,
    getCountryName,
    getSupportedCountry,
    isCountrySupported,
    currentCountry,
    supportedCountriesMap,
    supportedCountries,
    loading
  };
};
